import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetCentralStationOvernights } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

/**
 * Bilar som avslutade dagen på en centralstation (Company.IsCentralStation), i bil-dygn per
 * station: en bil räknas varje dag den står kvar, se
 * OperationsKpiLogic.GetCentralStationOvernights.
 */
const CentralStationOvernightsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetCentralStationOvernights();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;
  const stations = result?.byStation ?? [];

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.CentralStationOvernights]}
      count={undefined}
      isLoading={getData.isLoading}
      info="Bilar som stod på en centralstation (företag med kryssrutan Centralstation) vid dagens slut, räknat i bil-dygn: en bil räknas från dagen den ställdes där till dagen före den kördes vidare. Har vidaretransporten inte startat räknas bilen som kvar till idag, högst 30 dygn. Etappen dit känns igen på stationens adress eller på att den slutar inom 300 m från stationen. Länet är stationens."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!result || result.carDays === 0}
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{nf(result?.averageCarsPerDay ?? 0, 1)}</strong> bilar per
          dygn på centralstationerna · {nf(result?.carDays ?? 0)} bil-dygn på{' '}
          {nf(result?.cars ?? 0)} bilar, {nf(result?.averageDaysPerCar ?? 0, 1)}{' '}
          dygn per bil
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Centralstation</th>
              <th style={thStyle}>Län</th>
              <th style={thNumStyle}>Bil-dygn</th>
              <th style={thNumStyle}>Bilar</th>
              <th style={thNumStyle}>Dygn per bil</th>
              <th style={thNumStyle}>Andel</th>
            </tr>
          </thead>
          <tbody>
            {stations.map((row) => (
              <tr
                key={row.stationID}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <td style={tdStyle}>{row.stationName}</td>
                <td style={tdStyle}>{row.countyName}</td>
                <td style={tdNumStyle}>{nf(row.carDays)}</td>
                <td style={tdNumStyle}>{nf(row.cars)}</td>
                <td style={tdNumStyle}>{nf(row.averageDaysPerCar, 1)}</td>
                <td style={tdNumStyle}>
                  {pct(row.shareOfAllCarDaysPercentage)}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdStyle}>–</td>
              <td style={tdNumStyle}>{nf(result?.carDays ?? 0)}</td>
              <td style={tdNumStyle}>{nf(result?.cars ?? 0)}</td>
              <td style={tdNumStyle}>
                {nf(result?.averageDaysPerCar ?? 0, 1)}
              </td>
              <td style={tdNumStyle}>{result?.carDays ? '100,0 %' : '–'}</td>
            </tr>
          </tbody>
        </table>

        <ControlFigures
          items={[
            `${nf(result?.stations ?? 0)} flaggade centralstationer, ${nf(result?.stationsWithoutAddressOrPosition ?? 0)} utan adress och position`,
            `${nf(result?.carsMatchedByPosition ?? 0)} bilar igenkända på position i stället för adress`,
            `${nf(result?.continuedSameDay ?? 0)} ankomster där bilen kördes vidare samma dag räknas inte`,
            `${nf(result?.staysWithoutNextLeg ?? 0)} ankomster utan senare etapp räknas bara ankomstdagen`,
            `${nf(result?.staysStillParked ?? 0)} bilar vars vidaretransport inte startat räknas som kvar till idag (${nf(result?.staysCapped ?? 0)} kapade vid ${nf(result?.maxParkedDays ?? 30)} dygn)`,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default CentralStationOvernightsWidget;
